import { Component, computed, input, output } from '@angular/core';
import { DocumentIcon } from '../document-icon/document-icon';

const INDEXING_STATUS_CONFIG: Record<
  string,
  { label: string; classes: string; spinning?: boolean }
> = {
  NotIndexed: {
    label: 'Non indicizzato',
    classes: 'bg-slate-100 text-slate-500',
  },
  InProgress: {
    label: 'In corso',
    classes: 'bg-blue-100 text-blue-600',
    spinning: true,
  },
  Completed: { label: 'Indicizzato', classes: 'bg-green-100 text-green-700' },
  Failed: {
    label: 'Errore indicizzazione',
    classes: 'bg-amber-100 text-amber-700',
  },
};

const REINDEXABLE_STATUSES = new Set(['Failed', 'NotIndexed', 'Cancelled']);

@Component({
  selector: 'lib-attachment-item',
  imports: [DocumentIcon],
  template: `<li
    class="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-2.5 py-2"
    data-tour="attachment-item"
  >
    <lib-document-icon [fileName]="fileName()" />

    <span
      class="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-base font-medium text-slate-800"
    >
      {{ fileName() }}
    </span>

    @if (statusConfig(); as cfg) {
      <span
        class="flex flex-shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium {{
          cfg.classes
        }}"
        [title]="indexingError() ?? cfg.label"
        data-tour="attachment-status"
      >
        @if (cfg.spinning) {
          <i class="pi pi-spin pi-spinner text-xs"></i>
        }
        {{ cfg.label }}
      </span>
    }

    @if (loading()) {
      <i
        class="pi pi-spin pi-spinner size-4 flex-shrink-0 text-lg text-slate-400"
      ></i>
    } @else {
      @if (canReindex()) {
        <button
          class="flex size-7 flex-shrink-0 items-center justify-center rounded-md border-none bg-transparent text-slate-400 transition hover:bg-blue-100 hover:text-blue-500"
          title="Reindicizza"
          (click)="reindexClick.emit()"
        >
          <i class="pi pi-refresh text-lg"></i>
        </button>
      }

      @if (showDownload()) {
        <a
          [href]="downloadUrl()"
          target="_blank"
          class="flex size-7 flex-shrink-0 items-center justify-center rounded-md text-slate-400 no-underline transition hover:bg-slate-200 hover:text-slate-600"
          title="Apri"
        >
          <i class="pi pi-external-link text-lg"></i>
        </a>
      }

      @if (showDelete()) {
        <button
          class="flex size-7 flex-shrink-0 items-center justify-center rounded-md border-none bg-transparent text-slate-400 transition hover:bg-red-100 hover:text-red-500"
          title="Rimuovi"
          data-tour="attachment-delete"
          (click)="deleteClick.emit()"
        >
          <i class="pi pi-times text-lg"></i>
        </button>
      }
    }
  </li>`,
})
export class AttachmentItem {
  readonly fileName = input.required<string>();
  readonly downloadUrl = input<string>();
  readonly loading = input(false);
  readonly showDownload = input(false);
  readonly showDelete = input(false);
  /** Off hides the re-index button even for a failed index (read-only views). */
  readonly showReindex = input(true);
  readonly indexingStatus = input<string | null | undefined>();
  readonly indexingError = input<string | null | undefined>();

  readonly deleteClick = output();
  readonly reindexClick = output();

  protected readonly statusConfig = computed(
    () => INDEXING_STATUS_CONFIG[this.indexingStatus() ?? ''] ?? null,
  );

  protected readonly canReindex = computed(
    () =>
      this.showReindex() &&
      REINDEXABLE_STATUSES.has(this.indexingStatus() ?? ''),
  );
}
